import { Box, Badge, Button, useTheme, Chip, Divider, Stack, Popover, Typography, Checkbox, FormControlLabel, FormGroup, } from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { useEffect, useState } from "react";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { ApiRequestFailed, authenticateAndFetchData, retryFn } from "@lib/apis";
import { useQuery } from "@tanstack/react-query";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { ArrowBack, Downloading, FilterList, Clear } from "@mui/icons-material";
import { OnboardingSourceChart } from "./components/OnboardingSourceChart";
import { AdminDiscoveryOptionManager } from "./components/AdminDiscoveryOptionManager";

interface OnboardingResponse {
    id: number;
    user_id: number;
    user_email: string;
    source_key: string;
    source_label: string;
    other_text: string | null;
    created_at: string;
}

interface PaginationData {
    data: OnboardingResponse[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
}

interface OptionItem {
    id: number;
    key: string;
    label: string;
}

interface SourceFilterButtonProps {
    selectedSources: string[];
    onSourceChange: (newSources: string[]) => void;
}

const SourceFilterButton: React.FC<SourceFilterButtonProps> = ({
    selectedSources,
    onSourceChange,
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const { data: optionsData } = useQuery({
        queryKey: ["adminOnboardingOptions"],
        queryFn: () => authenticateAndFetchData("/admin/onboarding-options/"),
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    const options: OptionItem[] = optionsData?.data?.options || [];

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleToggleSource = (key: string) => {
        if (selectedSources.includes(key)) {
            onSourceChange(selectedSources.filter((s) => s !== key));
        } else {
            onSourceChange([...selectedSources, key]);
        }
    };

    const handleClearAll = () => {
        onSourceChange([]);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <Badge badgeContent={selectedSources.length} color="primary">
                <Button
                    startIcon={<FilterList />}
                    variant={selectedSources.length > 0 ? "contained" : "outlined"}
                    color={selectedSources.length > 0 ? "primary" : "inherit"}
                    onClick={handleClick}
                    sx={{
                        borderColor: theme.palette.divider,
                        color: selectedSources.length > 0 ? undefined : theme.palette.text.primary,
                        "&:hover": {
                            borderColor: theme.palette.text.secondary,
                        },
                    }}
                >
                    Source Filter
                </Button>
            </Badge>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: 280,
                        maxHeight: 380,
                        bgcolor: "background.paper",
                        color: "text.primary",
                        boxShadow: theme.shadows[8],
                        backgroundImage: "none",
                        overflow: "hidden",
                    },
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography fontWeight={600} variant="subtitle2" color="text.primary">
                        Filter by Source
                    </Typography>
                    {selectedSources.length > 0 && (
                        <Button
                            size="small"
                            color="error"
                            startIcon={<Clear />}
                            onClick={handleClearAll}
                            sx={{ fontSize: 11 }}
                        >
                            Clear
                        </Button>
                    )}
                </Stack>
                <Divider sx={{ mb: 1, borderColor: "divider" }} />

                <FormGroup
                    sx={{
                        maxHeight: 280,
                        display: "flex",
                        flexDirection: "column",
                        flexWrap: "nowrap",
                        overflowY: "auto",
                        overflowX: "hidden",
                        pr: 0.5,
                    }}
                >
                    {options.map((opt) => (
                        <FormControlLabel
                            key={opt.key}
                            sx={{
                                width: "100%",
                                mx: 0,
                                my: 0.25,
                            }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedSources.includes(opt.key)}
                                    onChange={() => handleToggleSource(opt.key)}
                                    sx={{ py: 0.5 }}
                                />
                            }
                            label={
                                <Typography
                                    variant="body2"
                                    color="text.primary"
                                    noWrap
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {opt.label}
                                </Typography>
                            }
                        />
                    ))}
                </FormGroup>
            </Popover>
        </>
    );
};

export default function AdminOnboardingQuestionResponses() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [pageData, setPageData] = useState<PaginationData>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [search, setSearch] = useState("");
    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [exportLoading, setExportLoading] = useState(false);

    const sourcesQueryParam = selectedSources.join(",");

    const adminOnboardingResponsesQuery = useQuery({
        queryKey: [
            "adminOnboardingResponsesQuery",
            page,
            pageSize,
            search,
            sourcesQueryParam,
        ],
        queryFn: () =>
            authenticateAndFetchData(
                `/admin/onboarding-question-responses/?page=${page}&page_size=${pageSize}&search=${search}&sources=${sourcesQueryParam}`
            ),
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
        if (adminOnboardingResponsesQuery.data?.data) {
            const response = adminOnboardingResponsesQuery.data.data;

            setPageData({
                data: response.data || [],
                total_count: response.total_count,
                total_pages: response.total_pages,
                current_page: response.current_page,
                page_size: response.page_size,
            });
        }
    }, [adminOnboardingResponsesQuery.data]);

    function timestampFormatter(params: ValueFormatterParams) {
        if (!params.value) return "-";
        return format(new Date(params.value), "do MMM y, HH:mm:ss");
    }

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handleSourceChange = (newSources: string[]) => {
        setSelectedSources(newSources);
        setPage(1);
    };

    const columnCustomizerLabel = {
        user_email: "User Email",
        source_label: "Discovery Source",
        other_text: "Other Text",
        created_at: "Responded On",
    };

    const columnDefs: ColDef[] = [
        { field: "user_email", headerName: "User Email", flex: 1.5 },
        {
            field: "source_label",
            headerName: "Discovery Source",
            flex: 1,
            cellRenderer: (params: any) => (
                <Chip
                    label={params.value || params.data?.source_key}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            ),
        },
        {
            field: "other_text",
            headerName: "Other Text",
            flex: 1.5,
            valueFormatter: (params) => params.value || "-",
        },
        {
            field: "created_at",
            headerName: "Responded On",
            flex: 1,
            valueFormatter: timestampFormatter,
        },
    ];

    const responseColumns: CSVColumn<OnboardingResponse>[] = [
        { header: "User Email", accessor: "user_email" },
        { header: "Discovery Source", accessor: "source_label" },
        { header: "Other Text", accessor: (row) => row.other_text || "" },
        { header: "Responded On", accessor: "created_at" },
    ];

    const ExportResponsesCsvButton = () => {
        const handleExportResponses = async () => {
            setExportLoading(true);

            try {
                const response = await authenticateAndFetchData(
                    `/admin/onboarding-question-responses/?export_all=true&search=${search}&sources=${sourcesQueryParam}`
                );

                const responsesToExport = response?.data?.data || [];

                if (!responsesToExport.length) {
                    enqueueSnackbar("No response data available to export.", { variant: "error" });
                    return;
                }

                const success = exportToCSV(
                    responsesToExport,
                    responseColumns,
                    `onboarding_question_responses.csv`
                );

                if (success) {
                    enqueueSnackbar("Responses exported successfully!", { variant: "success" });
                } else {
                    enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
                }
            } catch (err) {
                console.error("Export error:", err);
                enqueueSnackbar("Error while exporting responses.", { variant: "error" });
            } finally {
                setExportLoading(false);
            }
        };

        return (
            <Button
                startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={handleExportResponses}
                disabled={exportLoading}
            >
                {exportLoading ? "Exporting..." : "Export"}
            </Button>
        );
    };

    if (!pageData && adminOnboardingResponsesQuery.isLoading) {
        return <PageLoading />;
    } else if (adminOnboardingResponsesQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={adminOnboardingResponsesQuery.error as unknown as ApiRequestFailed} />;
    } else if (pageData) {
        return (
            <Stack
                direction="column"
                spacing={2}
                sx={{
                    pb: 4,
                    width: "100%",
                    minHeight: "100%",
                    overflowY: "auto",
                    bgcolor: "background.default",
                    color: "text.primary",
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate(-1)} color="inherit">
                        <ArrowBack />
                    </IconButton>
                    <Typography fontWeight="bold" variant="h5" color="text.primary">
                        Onboarding Question Responses
                    </Typography>
                </Stack>
                <Divider sx={{ borderColor: "divider" }} />

                <AdminDiscoveryOptionManager />
                <OnboardingSourceChart />

                <Box
                    sx={{
                        height: 550,
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 400,
                    }}
                >
                    <PaginatedServerSideDataTable
                        columns={columnDefs}
                        paginationData={pageData}
                        loading={adminOnboardingResponsesQuery.isFetching}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search email or source..."
                        columnLabels={columnCustomizerLabel}
                        actions={
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <SourceFilterButton
                                    selectedSources={selectedSources}
                                    onSourceChange={handleSourceChange}
                                />
                                <ExportResponsesCsvButton />
                            </Stack>
                        }
                    />
                </Box>
            </Stack>
        );
    } else {
        return <></>;
    }
}